import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';   // Feb-2026 · i18n Wave 3
import { PanelFrame } from '../workspace/PanelFrame';
import { BookText, Plus, CheckCircle2, Circle, FileEdit, FileQuestion, Flag, Loader2, PartyPopper, Pencil, Hash, Type, TrendingUp, Wand2, Mic, GitBranch, Edit3, Check, X, Trash2, Eye, Zap, Lock, BookOpen, ShieldCheck, Upload, ChevronDown, MoreHorizontal } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePW } from '@/lib/store';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { chaptersApi, projectsApi, revisionApi } from '@/lib/api';
import { creativeInteractionApi } from '@/lib/api';
import { toast } from 'sonner';
import CoachHint from '@/components/coach/CoachHint';
import CoachHintDialog from '@/components/coach/CoachHintDialog';
import { chapterRowHint } from '@/components/coach/coachHints';
import { chapterLabel } from '@/lib/chapterLabel';
import AnalyzeTransitionsDrawer from './AnalyzeTransitionsDrawer';
import VoiceCoachDrawer from './VoiceCoachDrawer';
import ArcWalkerDrawer from './ArcWalkerDrawer';
import WalkerDrawer from './WalkerDrawer';
import ContinuityReviewDrawer from './ContinuityReviewDrawer';
import VoiceRewritePreviewDialog from '../dialogs/VoiceRewritePreviewDialog';   // iter-p3-w
import { useAuth } from '@/lib/auth_context';
import { useCapabilities } from '@/lib/useCapabilities';
import axios from 'axios';
import PlotReadinessPill, { PlotCompletenessBanner } from './PlotReadinessPill';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const STATUS = {
  final:   { color: 'success',          icon: CheckCircle2, tKey: 'chapters.statusFinal' },
  draft:   { color: 'primary',          icon: FileEdit,     tKey: 'chapters.statusDraft' },
  outline: { color: 'chart-2',          icon: FileQuestion, tKey: 'chapters.statusOutline' },
  planned: { color: 'muted-foreground', icon: Circle,       tKey: 'chapters.statusPlanned' },
};

// ── SeriesSettingsRow ──────────────────────────────────────────────────────
// Compact toggle + book-number inputs + Voice Lock auto-link rendered
// inside the Chapters panel. Saving is immediate via updateProject.
function SeriesSettingsRow({ project, updateProject }) {
  const isSeries     = !!project?.is_series;
  const bookNum      = project?.series_book_number ?? 1;
  const totalBooks   = project?.series_total_books  ?? '';
  const [saving, setSaving]           = useState(false);
  // Voice-lock auto-link state
  const [linkStatus, setLinkStatus]   = useState(null);   // {source, candidates}
  const [linkLoading, setLinkLoading] = useState(false);
  const [linking, setLinking]         = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState('');

  // Fetch link-status whenever series settings change
  useEffect(() => {
    if (!isSeries || !bookNum || bookNum < 2 || !project?.id) {
      setLinkStatus(null);
      return;
    }
    let cancelled = false;
    setLinkLoading(true);
    const API = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${API}/api/projects/${project.id}/series-voice/link-status`,
      { withCredentials: true })
      .then((r) => { if (!cancelled) setLinkStatus(r.data); })
      .catch(() => { if (!cancelled) setLinkStatus(null); })
      .finally(() => { if (!cancelled) setLinkLoading(false); });
    return () => { cancelled = true; };
  }, [project?.id, isSeries, bookNum]);

  const save = async (patch) => {
    if (!updateProject) return;
    setSaving(true);
    try { await updateProject(patch); }
    catch { /* silent */ }
    finally { setSaving(false); }
  };

  const handleAutoLink = async () => {
    const srcId = selectedCandidateId || linkStatus?.candidates?.find(c => c.eligible)?.id;
    if (!srcId || !project?.id) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    setLinking(true);
    try {
      await axios.post(
        `${API}/api/projects/${project.id}/series-voice/designate`,
        { source_project_id: srcId },
        { withCredentials: true },
      );
      const srcName = linkStatus?.candidates?.find(c => c.id === srcId)?.name || 'previous book';
      toast.success(`Voice locked to "${srcName}".`);
      // Refresh status
      const r = await axios.get(
        `${API}/api/projects/${project.id}/series-voice/link-status`,
        { withCredentials: true },
      );
      setLinkStatus(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (err?.response?.status === 403) {
        toast.error('Voice Lock requires a Premier plan. Upgrade in Settings → Billing.');
      } else {
        toast.error(detail || 'Failed to link voice source.');
      }
    } finally {
      setLinking(false);
    }
  };

  const handleClearLink = async () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    try {
      await axios.post(
        `${API}/api/projects/${project.id}/series-voice/clear`,
        {},
        { withCredentials: true },
      );
      setLinkStatus((prev) => prev ? { ...prev, source: null } : null);
      toast.success('Voice source cleared.');
    } catch {
      toast.error('Failed to clear voice source.');
    }
  };

  const eligibleCandidates = linkStatus?.candidates?.filter(c => c.eligible) ?? [];
  const ineligibleCandidates = linkStatus?.candidates?.filter(c => !c.eligible) ?? [];

  return (
    <div
      className="border-b border-panel-border px-3 py-2"
      data-testid="series-settings-row"
    >
      {/* ── Toggle row ── */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] uppercase tracking-wider text-muted-foreground select-none">
          Series
        </span>
        <button
          role="switch"
          aria-checked={isSeries}
          onClick={() => save({ is_series: !isSeries })}
          title={isSeries ? 'Series mode active — final chapter plants a hook' : 'Mark as part of a series'}
          className={cn(
            'relative inline-flex h-4 w-7 shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-primary/40',
            isSeries ? 'bg-primary' : 'bg-secondary'
          )}
          data-testid="series-toggle"
        >
          <span className={cn(
            'pointer-events-none inline-block h-3 w-3 rounded-full bg-white shadow transition-transform',
            isSeries ? 'translate-x-3.5' : 'translate-x-0.5'
          )} />
        </button>
      </div>

      {/* ── Book number + total ── */}
      {isSeries && (
        <div className="mt-1.5 flex items-center gap-1.5 text-[13px]">
          <span className="text-muted-foreground">Book</span>
          <input
            type="number" min={1} max={100}
            value={bookNum}
            onChange={(e) => save({ series_book_number: Math.max(1, parseInt(e.target.value) || 1) })}
            className="w-10 rounded border border-border bg-background px-1 py-0.5 text-center text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            data-testid="series-book-number"
          />
          <span className="text-muted-foreground">of</span>
          <input
            type="number" min={2} max={100}
            placeholder="?"
            value={totalBooks}
            onChange={(e) => {
              const v = parseInt(e.target.value);
              save({ series_total_books: isNaN(v) ? null : Math.max(2, v) });
            }}
            className="w-10 rounded border border-border bg-background px-1 py-0.5 text-center text-[13px] text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary/50"
            data-testid="series-total-books"
          />
          {saving && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
          <span className="ml-1 text-[13px] text-muted-foreground/70">Final ch → hook</span>
        </div>
      )}

      {/* ── Voice Lock auto-link (Book 2+) ── */}
      {isSeries && bookNum >= 2 && (
        <div className="mt-2 space-y-1.5" data-testid="voice-lock-section">

          {/* Currently linked source */}
          {linkStatus?.source ? (
            <div className="flex items-center gap-1.5 rounded border border-emerald-300/60 bg-emerald-50 px-2 py-1 text-[13px]"
              data-testid="voice-lock-active">
              <BookOpen className="h-3 w-3 shrink-0 text-emerald-700" />
              <span className="flex-1 truncate text-emerald-900 font-medium">
                Voice: {linkStatus.source.name}
              </span>
              <button
                onClick={handleClearLink}
                className="shrink-0 text-emerald-700 hover:text-red-600 transition-colors"
                title="Clear voice source"
                data-testid="voice-lock-clear"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : linkLoading ? (
            <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Checking for Book {bookNum - 1}…</span>
            </div>
          ) : eligibleCandidates.length > 0 ? (
            /* Auto-link available */
            <div className="space-y-1" data-testid="voice-lock-candidates">
              {eligibleCandidates.length > 1 && (
                <select
                  value={selectedCandidateId}
                  onChange={(e) => setSelectedCandidateId(e.target.value)}
                  className="w-full rounded border border-border bg-background px-2 py-0.5 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                  data-testid="voice-lock-candidate-picker"
                >
                  <option value="">— pick Book {bookNum - 1} —</option>
                  {eligibleCandidates.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({(c.words).toLocaleString()} words)
                    </option>
                  ))}
                </select>
              )}
              <button
                onClick={handleAutoLink}
                disabled={linking || (eligibleCandidates.length > 1 && !selectedCandidateId)}
                className="flex w-full items-center justify-center gap-1.5 rounded border border-primary/40 bg-primary/5 px-2 py-1 text-[13px] font-medium text-primary transition-colors hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="voice-lock-auto-link"
              >
                {linking
                  ? <><Loader2 className="h-3 w-3 animate-spin" /> Linking…</>
                  : <><BookOpen className="h-3 w-3" />
                    Auto-link Book {bookNum - 1}
                    {eligibleCandidates.length === 1 ? `: "${eligibleCandidates[0].name}"` : ' voice'}
                  </>}
              </button>
            </div>
          ) : ineligibleCandidates.length > 0 ? (
            /* Candidates found but not enough words */
            <p className="text-[13px] text-amber-700" data-testid="voice-lock-insufficient">
              Book {bookNum - 1} found but needs ≥ 5,000 drafted words for Voice Lock
              ({(ineligibleCandidates[0]?.words || 0).toLocaleString()} written so far).
            </p>
          ) : linkStatus !== null ? (
            /* No matching Book N-1 in the user's projects */
            <p className="text-[13px] text-muted-foreground" data-testid="voice-lock-no-candidates">
              No Book {bookNum - 1} found. Mark that project as Book {bookNum - 1} in its
              Series row to enable auto-linking.
            </p>
          ) : null}

        </div>
      )}
    </div>
  );
}

// ── VoiceSampleRow ─────────────────────────────────────────────────────────
// Collapsible textarea + strength selector (1–5) + inline preview drawer.
// Saved to the project via updateProject with a 1.2s debounce on the text.
// Strength saves immediately on click. Works for ALL projects.
const STRENGTH_LABELS = {
  1: ['Loose',    'Light reference — AI adapts freely'],
  2: ['Gentle',   'Tone influence — variation welcome'],
  3: ['Balanced', 'Mirror cadence, tone, register'],
  4: ['Strong',   'Close match — rhythm and vocabulary'],
  5: ['Exact',    'High-fidelity replication'],
};

function VoiceSampleRow({ project, updateProject }) {
  const sample   = project?.custom_voice_sample ?? '';
  const strength = project?.voice_sample_strength ?? 3;
  const [open, setOpen]           = useState(!!sample);
  const [draft, setDraft]         = useState(sample);
  const [saving, setSaving]       = useState(false);
  const [preview, setPreview]     = useState(null);   // null | '' | string
  const [prevLoading, setPrevLoading] = useState(false);
  const timerRef = useRef(null);

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Sync draft if the project reloads externally
  useEffect(() => { setDraft(project?.custom_voice_sample ?? ''); }, [project?.custom_voice_sample]);

  const flush = async (value) => {
    if (!updateProject) return;
    clearTimeout(timerRef.current);
    setSaving(true);
    try { await updateProject({ custom_voice_sample: value || null }); }
    catch { /* silent */ }
    finally { setSaving(false); }
  };

  const handleChange = (e) => {
    const v = e.target.value.slice(0, 6000);
    setDraft(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flush(v), 1200);
    // Reset preview so stale preview isn't shown after edits
    setPreview(null);
  };

  const saveStrength = async (val) => {
    if (!updateProject) return;
    try { await updateProject({ voice_sample_strength: val }); }
    catch { /* silent */ }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !project?.id) return;
    e.target.value = '';           // reset so same file can be re-uploaded
    const API = process.env.REACT_APP_BACKEND_URL;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(
        `${API}/api/projects/${project.id}/voice-sample/upload`,
        form,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      // Reload sample from project context after save
      const truncMsg = data.truncated ? ` (trimmed from ${data.original_len.toLocaleString()})` : '';
      toast.success(`Uploaded "${file.name}" — ${data.chars.toLocaleString()} chars${truncMsg}`);
      // Force VoiceSampleRow to reflect saved sample via project reload
      if (updateProject) await updateProject({});   // triggers project refresh
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Upload failed.';
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const loadPreview = async () => {
    if (preview !== null) { setPreview(null); return; } // toggle off
    const API = process.env.REACT_APP_BACKEND_URL;
    setPrevLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/projects/${project.id}/series-voice/preview`,
        { withCredentials: true },
      );
      setPreview(data.preview || '');
    } catch {
      setPreview('');
    } finally {
      setPrevLoading(false);
    }
  };

  return (
    <div className="border-b border-panel-border px-3 py-2" data-testid="voice-sample-row">
      {/* ── Header row ── */}
      <div className="flex items-center justify-between gap-2">
        <button
          className="flex flex-1 items-center gap-1.5 text-left"
          onClick={() => setOpen((o) => !o)}
          data-testid="voice-sample-toggle"
        >
          <span className="text-[13px] uppercase tracking-wider text-muted-foreground select-none">
            Voice Sample
          </span>
          {sample && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" title="Sample saved" />}
          {saving && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
        </button>
        {/* Preview button — always visible when there's a sample or cross-book lock */}
        {(sample || project?.series_voice_source_project_id) && (
          <button
            onClick={loadPreview}
            disabled={prevLoading}
            className={cn(
              'flex items-center gap-1 rounded px-1.5 py-0.5 text-[13px] transition-colors',
              preview !== null
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground',
            )}
            title="Preview the exact text the AI will read"
            data-testid="voice-sample-preview-btn"
          >
            {prevLoading
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <BookOpen className="h-3 w-3" />}
            <span>{preview !== null ? 'Hide' : 'Preview'}</span>
          </button>
        )}
      </div>

      {/* ── Textarea + strength controls ── */}
      {open && (
        <div className="mt-1.5 space-y-2">
          <p className="text-[13px] text-muted-foreground leading-relaxed">
            Paste 3–5 paragraphs of your own writing. The AI mirrors your style in every chapter draft.
          </p>
          <textarea
            value={draft}
            onChange={handleChange}
            placeholder="Paste a passage that captures your ideal voice…"
            rows={6}
            maxLength={6000}
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-[13.5px] text-foreground placeholder:text-muted-foreground/60 resize-none focus:outline-none focus:ring-1 focus:ring-primary/50"
            data-testid="voice-sample-textarea"
          />

          {/* Strength pills with tooltips */}
          <div className="space-y-1" data-testid="voice-strength-controls">
            <div className="flex items-center justify-between">
              <span className="text-[13px] text-muted-foreground">Adherence</span>
              <span className="text-[13px] text-muted-foreground">
                {STRENGTH_LABELS[strength]?.[0] ?? ''}
              </span>
            </div>
            <TooltipProvider delayDuration={300}>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Tooltip key={n}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => saveStrength(n)}
                        data-testid={`voice-strength-${n}`}
                        className={cn(
                          'flex h-6 flex-1 items-center justify-center rounded text-[13px] font-semibold transition-colors',
                          n === strength
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-secondary text-muted-foreground hover:bg-secondary/80',
                        )}
                      >
                        {n}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[200px] text-center text-[13px]">
                      <span className="font-semibold">{n} — {STRENGTH_LABELS[n][0]}</span>
                      <br />{STRENGTH_LABELS[n][1]}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </TooltipProvider>
          </div>

          {/* Upload file button */}
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.docx"
              className="hidden"
              onChange={handleFileUpload}
              data-testid="voice-sample-file-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 rounded border border-border bg-background px-2 py-1 text-[13px] text-muted-foreground transition-colors hover:text-foreground hover:border-foreground/40 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="voice-sample-upload-btn"
            >
              {uploading
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Upload className="h-3 w-3" />}
              Upload .txt or .docx
            </button>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-[13px] text-muted-foreground">{draft.length.toLocaleString()} / 6,000</span>
            {draft && (
              <button
                onClick={() => { setDraft(''); flush(''); setPreview(null); }}
                className="text-[13px] text-muted-foreground hover:text-destructive transition-colors"
                data-testid="voice-sample-clear"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Inline preview drawer ── */}
      {preview !== null && (
        <div
          className="mt-2 rounded border border-border bg-muted/30 px-2.5 py-2"
          data-testid="voice-sample-preview-drawer"
        >
          <p className="mb-1.5 text-[13px] font-medium text-muted-foreground uppercase tracking-wide">
            What the AI will read (system prompt injection)
          </p>
          {preview ? (
            <pre className="whitespace-pre-wrap break-words text-[13px] text-foreground/80 leading-relaxed max-h-48 overflow-y-auto">
              {preview}
            </pre>
          ) : (
            <p className="text-[13px] text-muted-foreground italic">
              No voice sample configured yet — paste some prose above or link a Voice Lock source.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── WordRing ───────────────────────────────────────────────────────────────
// Tiny SVG progress ring for per-chapter word-count. Shows progress from 0→target.
function WordRing({ words = 0, target = 2500, 'data-testid': testId }) {
  const pct = target > 0 ? Math.min(100, (words / target) * 100) : 0;
  const r = 7;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const done = pct >= 100;
  return (
    <svg
      width="18" height="18" viewBox="0 0 18 18"
      className="shrink-0"
      aria-label={`${Math.round(pct)}% of chapter target`}
      data-testid={testId}
    >
      {/* Track */}
      <circle cx="9" cy="9" r={r} fill="none" strokeWidth="2"
        className="stroke-secondary" />
      {/* Progress arc */}
      {pct > 0 && (
        <circle cx="9" cy="9" r={r} fill="none" strokeWidth="2"
          stroke={done ? '#22c55e' : 'hsl(var(--primary))'}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 9 9)" />
      )}
    </svg>
  );
}

export function ChaptersPanel({ onClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { chapters, activeChapterId, setActiveChapterId, project, setChapters, loadProject, updateProject,
          streamingChapterId, building } = usePW();
  const [savingId, setSavingId] = useState(null);
  const titlesVisible = project?.chapter_titles_visible !== false;
  const [togglingTitles, setTogglingTitles] = useState(false);
  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  // iter55f94 — Voice Coach drawer (prose + character).
  const [voiceOpen, setVoiceOpen] = useState(false);
  // Listen for cross-component open request (e.g. right-click context menu)
  useEffect(() => {
    const onOpen = () => setVoiceOpen(true);
    window.addEventListener('loom:open-voice-coach', onOpen);
    return () => window.removeEventListener('loom:open-voice-coach', onOpen);
  }, []);
  // iter-studio-premier — Voice Coach deep-link handoff from
  // PublishToStoreDialog. When it stores an intent in localStorage
  // and navigates here, auto-open the drawer with the flagged
  // chapters preloaded.
  const [voiceDeepLink, setVoiceDeepLink] = useState(null);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('pw_voice_coach_deeplink');
      if (!raw) return;
      const dl = JSON.parse(raw);
      if (project?.id && dl.project_id === project.id
          && Date.now() - Number(dl.at || 0) < 5 * 60 * 1000) {
        setVoiceDeepLink(dl);
        setVoiceOpen(true);
      }
      window.localStorage.removeItem('pw_voice_coach_deeplink');
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);
  // iter55f92 — Per-chapter count of pending "Fix weak transition"
  // suggestions. Refetches every 20s and whenever the analyze drawer
  // closes so writers can see the badges update after clicking
  // "Fix all weak transitions".
  const [weakByChapter, setWeakByChapter] = useState({});
  useEffect(() => {
    if (!project?.id) { setWeakByChapter({}); return undefined; }
    let cancelled = false;
    const fetchWeak = async () => {
      try {
        const r = await axios.get(`${API}/projects/${project.id}/suggestions`,
          { params: { status: 'pending' }, withCredentials: true, timeout: 15000 });
        if (cancelled) return;
        const counts = {};
        for (const s of (r.data || [])) {
          if (s.kind !== 'edit') continue;
          if (s.source !== 'transition-analyzer' && s.source !== 'voice-coach') continue;
          counts[s.chapter_id] = (counts[s.chapter_id] || 0) + 1;
        }
        setWeakByChapter(counts);
      } catch {
        if (!cancelled) setWeakByChapter({});
      }
    };
    fetchWeak();
    const iv = setInterval(fetchWeak, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [project?.id, analyzeOpen]);
  const totalWeak = useMemo(
    () => Object.values(weakByChapter).reduce((a, b) => a + b, 0),
    [weakByChapter],
  );
  const toggleTitlesVisible = async () => {
    if (!project?.id || togglingTitles) return;
    setTogglingTitles(true);
    try {
      await updateProject({ chapter_titles_visible: !titlesVisible });
      toast.success(!titlesVisible ? 'Showing chapter titles.' : 'Numbered chapters only.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update preference.');
    } finally { setTogglingTitles(false); }
  };
  // iter55f86 — Inline chapter rename. Double-click a title (or hit
  // the pencil icon that shows on hover) to edit it directly. Enter
  // commits, Esc cancels, blur commits after 200 ms so accidental
  // click-outs don't nuke the edit.
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const renameInputRef = useRef(null);
  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);
  const startRename = (c, e) => {
    if (e) { e.stopPropagation(); }
    setRenamingId(c.id);
    setRenameDraft(c.title || `Chapter ${c.number}`);
  };
  const cancelRename = () => { setRenamingId(null); setRenameDraft(''); };
  const commitRename = async (c) => {
    const next = (renameDraft || '').trim().slice(0, 120);
    setRenamingId(null);
    if (!next || next === c.title) return;
    setSavingId(c.id);
    try {
      const updated = await chaptersApi.update(c.id, { title: next });
      setChapters((prev) => prev.map((row) => row.id === c.id ? { ...row, ...updated } : row));
      toast.success('Chapter renamed.');
      // Silent creative interaction — chapter rename
      if (project?.id) {
        creativeInteractionApi.recordChapterEvent(project.id, {
          scope: 'chapter_rename',
          chapter_id: c.id,
          chapter_title: next,
          change_details: { old_title: c.title, new_title: next },
        }).catch(() => {});
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rename failed.');
    } finally { setSavingId(null); }
  };
  // iter-p2-p — Journey heatmap. Reader drop-off % per chapter,
  // fetched once per project. Silent when the project isn't
  // published or has no reader progress yet.
  const [heatByChapter, setHeatByChapter] = useState({});
  useEffect(() => {
    if (!project?.id) { setHeatByChapter({}); return undefined; }
    let cancelled = false;
    const fetchHeat = async () => {
      try {
        const r = await axios.get(`${API}/analytics/journey-by-project/${project.id}`,
          { withCredentials: true, timeout: 15000 });
        if (cancelled) return;
        const m = {};
        for (const it of (r.data?.items || [])) {
          if (it.drop_pct != null) m[it.chapter] = { drop_pct: it.drop_pct, drop_to_next: it.drop_to_next };
        }
        setHeatByChapter(m);
      } catch {
        if (!cancelled) setHeatByChapter({});
      }
    };
    fetchHeat();
    return () => { cancelled = true; };
  }, [project?.id]);

  // iter-p3-u — AI-provenance share per chapter (badge next to title).
  const [aiByChapter, setAiByChapter] = useState({});
  useEffect(() => {
    if (!project?.id) { setAiByChapter({}); return undefined; }
    let cancelled = false;
    const fetchAi = async () => {
      try {
        const r = await axios.get(
          `${API}/projects/${project.id}/editing-record/per-chapter`,
          { withCredentials: true, timeout: 15000 },
        );
        if (cancelled) return;
        setAiByChapter(r.data?.chapters || {});
      } catch {
        if (!cancelled) setAiByChapter({});
      }
    };
    fetchAi();
    const iv = setInterval(fetchAi, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [project?.id]);
  // iter-p3-u — Voice-drift check state per chapter.
  const [voiceDriftBusy, setVoiceDriftBusy] = useState(null);
  const [batchDriftBusy, setBatchDriftBusy] = useState(false);
  // Persist voice drift results for the session — badges stay on rows after check
  const [driftResults, setDriftResults] = useState({});

  // Load previously saved drift badges from MongoDB on mount / project change
  useEffect(() => {
    if (!project?.id) return;
    projectsApi.driftBadges(project.id)
      .then(badges => { if (Object.keys(badges).length) setDriftResults(badges); })
      .catch(() => { /* silent — badges are a nice-to-have enhancement */ });
  }, [project?.id]);

  // Revision accepted-note counts — shown as a ✓ badge per chapter row.
  const [revisionCounts, setRevisionCounts] = useState({});
  useEffect(() => {
    if (!project?.id) { setRevisionCounts({}); return; }
    revisionApi.getSummary(project.id)
      .then((data) => setRevisionCounts(data.counts || {}))
      .catch(() => {});
  }, [project?.id]);

  // Live-update the badge when Revision Mode accepts or clears a note.
  useEffect(() => {
    const onAccepted = (e) => {
      const { chapterId, count } = e.detail || {};
      if (!chapterId) return;
      setRevisionCounts((prev) =>
        count > 0
          ? { ...prev, [chapterId]: count }
          : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== chapterId)),
      );
    };
    window.addEventListener('loom:revision-note-accepted', onAccepted);
    return () => window.removeEventListener('loom:revision-note-accepted', onAccepted);
  }, []);

  const runVoiceDriftBatchCheck = async () => {
    if (!project?.id || batchDriftBusy) return;
    const eligible = chapters.filter(c => (c.words || 0) > 50);
    if (!eligible.length) { toast.info('No chapters with content to check.'); return; }
    setBatchDriftBusy(true);
    const toastId = toast.loading(`Checking voice drift in ${eligible.length} chapter${eligible.length === 1 ? '' : 's'}…`);
    let drifted = 0, clean = 0, errors = 0;
    for (const c of eligible) {
      try {
        const r = await axios.post(
          `${API}/projects/${project.id}/chapters/${c.id}/voice-drift-check`,
          { tail_chars: 1500 },
          { withCredentials: true, timeout: 45000 },
        );
        const d = r.data || {};
        setDriftResults(prev => ({ ...prev, [c.id]: { drifted: !!d.drifted, severity: d.severity || null } }));
        if (d.drifted) drifted++; else clean++;
      } catch { errors++; }
    }
    toast.dismiss(toastId);
    if (drifted === 0) {
      toast.success(`All ${clean} chapters passed voice check.`);
    } else {
      toast.warning(`Voice sweep: ${drifted} chapter${drifted === 1 ? '' : 's'} drifted · ${clean} clean${errors ? ` · ${errors} skipped` : ''}`, { duration: 8000 });
    }
    setBatchDriftBusy(false);
  };

  const runVoiceDriftCheck = async (c, e) => {
    e?.stopPropagation?.();
    if (!project?.id || !c?.id || voiceDriftBusy) return;
    setVoiceDriftBusy(c.id);
    try {
      const r = await axios.post(
        `${API}/projects/${project.id}/chapters/${c.id}/voice-drift-check`,
        { tail_chars: 1500 },
        { withCredentials: true, timeout: 45000 },
      );
      const d = r.data || {};
      setDriftResults(prev => ({ ...prev, [c.id]: { drifted: !!d.drifted, severity: d.severity || null } }));

      if (d.reason) { toast.info(d.reason); return; }
      if (!d.drifted) { toast.success(`Ch ${c.number}: voice on-region.`); return; }
      const preview = (d.examples || []).slice(0, 2).map((s) => `“${s.slice(0, 60)}…”`).join(' · ');
      toast.warning(
        `Ch ${c.number} · ${d.severity} regional drift${preview ? ` — ${preview}` : ''}${d.suggestion ? `\n${d.suggestion}` : ''}`,
        {
          duration: 12000,
          // iter-p3-v — One-click rewrite-in-region straight from the toast.
          action: {
            label: 'Rewrite last paragraph in-region',
            onClick: () => runVoiceRewrite(c, d.character_id),
          },
        },
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Voice check failed.');
    } finally {
      setVoiceDriftBusy(null);
    }
  };

  const runVoiceRewrite = async (c, character_id) => {
    if (!project?.id || !c?.id) return;
    const id = toast.loading(`Rewriting Ch ${c.number} last paragraph in-region…`);
    try {
      const r = await axios.post(
        `${API}/projects/${project.id}/chapters/${c.id}/voice-rewrite`,
        { paragraphs: 1, character_id: character_id || null },
        { withCredentials: true, timeout: 60000 },
      );
      toast.dismiss(id);
      // iter-p3-w — Open a proper preview dialog instead of copying to clipboard.
      setVoiceRewrite({ chapter: c, rewrite: r.data || null });
    } catch (err) {
      toast.dismiss(id);
      toast.error(err?.response?.data?.detail || 'Voice rewrite failed.');
    }
  };
  // iter-p3-w — Preview dialog state.
  const [voiceRewrite, setVoiceRewrite] = useState(null);

  // ── Auto-suggest title nudge on chapter draft complete ──────────────────
  useEffect(() => {
    const onDraftComplete = (e) => {
      const ch = e?.detail?.chapter;
      if (!ch) return;
      // Find the matching chapter in state (or use the event payload)
      const found = chapters.find((x) => x.id === ch.id) || ch;
      toast.info(
        `Chapter ${found.number || ch.number} is drafted — add an evocative title?`,
        {
          duration: 10000,
          action: {
            label: 'Get 3 title ideas',
            onClick: () => suggestTitle(found),
          },
        },
      );
    };
    window.addEventListener('pw:chapter-draft-complete', onDraftComplete);
    return () => window.removeEventListener('pw:chapter-draft-complete', onDraftComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapters]);

  // ── Writing Coach ribbon event wiring ────────────────────────────────────
  // Allow the WorkflowRibbon (and WritingCoachCard) to open Arc / Edit walker
  // directly via custom events, without prop-drilling.
  useEffect(() => {
    const onArcWalker  = () => setArcWalkerOpen(true);
    const onEditWalker = () => setWalkerOpen(true);
    window.addEventListener('loom:open-arc-walker',  onArcWalker);
    window.addEventListener('loom:open-edit-walker', onEditWalker);
    return () => {
      window.removeEventListener('loom:open-arc-walker',  onArcWalker);
      window.removeEventListener('loom:open-edit-walker', onEditWalker);
    };
  }, []);

  // ── Add new chapter ──────────────────────────────────────────────────────
  const [addingChapter, setAddingChapter] = useState(false);
  const addChapter = async () => {
    if (!project?.id || addingChapter) return;
    setAddingChapter(true);
    try {
      const newChapter = await chaptersApi.create(project.id);
      setChapters((prev) => [...prev, newChapter]);
      setActiveChapterId(newChapter.id);
      toast.success(`Chapter ${newChapter.number} added.`);
      // Silent creative interaction — chapter added
      creativeInteractionApi.recordChapterEvent(project.id, {
        scope: 'chapter_add',
        chapter_id: newChapter.id,
        chapter_title: newChapter.title || `Chapter ${newChapter.number}`,
        change_details: { chapter_number: newChapter.number },
      }).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add chapter.');
    } finally {
      setAddingChapter(false);
    }
  };

  // ── Delete chapter with 5s undo window ───────────────────────────────────
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const deleteChapter = async (cid) => {
    if (deletingId) return;
    const chapter = chapters.find(c => c.id === cid);
    if (!chapter) return;
    // Optimistically remove from UI right away
    const prevChapters = [...chapters];
    setChapters((prev) => prev.filter(c => c.id !== cid).map((c, i) => ({ ...c, number: i + 1 })));
    if (activeChapterId === cid) {
      const remaining = chapters.filter(c => c.id !== cid);
      setActiveChapterId(remaining.length ? remaining[0].id : null);
    }
    setConfirmDeleteId(null);
    // 5-second undo window — actual API call is deferred
    let undone = false;
    const undoTimer = setTimeout(async () => {
      if (undone) return;
      setDeletingId(cid);
      try {
        await chaptersApi.delete(cid);
        // Silent creative interaction — chapter deleted (after undo window passes)
        if (project?.id) {
          creativeInteractionApi.recordChapterEvent(project.id, {
            scope: 'chapter_delete',
            chapter_id: cid,
            chapter_title: chapter.title || `Chapter ${chapter.number}`,
            change_details: { chapter_number: chapter.number },
          }).catch(() => {});
        }
      } catch (err) {
        // Re-insert on API failure
        setChapters(prevChapters);
        toast.error(err?.response?.data?.detail || 'Delete failed — chapter restored.');
      } finally {
        setDeletingId(null);
      }
    }, 5000);
    toast(`Chapter ${chapter.number} deleted.`, {
      duration: 5000,
      action: {
        label: 'Undo',
        onClick: () => {
          undone = true;
          clearTimeout(undoTimer);
          setChapters(prevChapters);
          if (activeChapterId !== cid) setActiveChapterId(cid);
          toast.success('Deletion undone.');
        },
      },
    });
  };

  // ── Global manuscript rename ─────────────────────────────────────────────
  const [globalRenaming, setGlobalRenaming] = useState(false);
  const [globalRenameDraft, setGlobalRenameDraft] = useState('');
  const [globalRenameSaving, setGlobalRenameSaving] = useState(false);
  const globalRenameRef = useRef(null);
  useEffect(() => {
    if (globalRenaming && globalRenameRef.current) {
      globalRenameRef.current.focus();
      globalRenameRef.current.select();
    }
  }, [globalRenaming]);
  const startGlobalRename = () => {
    setGlobalRenameDraft(project?.title || project?.name || '');
    setGlobalRenaming(true);
  };
  const cancelGlobalRename = () => { setGlobalRenaming(false); setGlobalRenameDraft(''); };
  const commitGlobalRename = async () => {
    const next = (globalRenameDraft || '').trim().slice(0, 200);
    const current = project?.title || project?.name || '';
    if (!next || next === current) { cancelGlobalRename(); return; }
    setGlobalRenameSaving(true);
    try {
      const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
      await axios.post(
        `${API_URL}/projects/${project.id}/global-rename`,
        { title: next },
        { withCredentials: true },
      );
      // Sync into local store via updateProject
      if (updateProject) {
        await updateProject({ title: next, name: next });
      }
      toast.success(`Manuscript renamed to "${next}".`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rename failed.');
    } finally {
      setGlobalRenameSaving(false);
      setGlobalRenaming(false);
    }
  };

  // ── AI chapter title suggester ────────────────────────────────────────────
  const [suggestingTitleId, setSuggestingTitleId] = useState(null);
  const [titleSuggestion, setTitleSuggestion] = useState(null); // {chapter_id, suggestions: [{title, reasoning}]}
  const suggestTitle = async (c, e) => {
    e?.stopPropagation?.();
    if (!c?.id || suggestingTitleId) return;
    setSuggestingTitleId(c.id);
    const toastId = toast.loading(`Getting 3 title ideas for Ch ${c.number}…`);
    try {
      const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
      const r = await axios.post(
        `${API_URL}/projects/chapters/${c.id}/suggest-title`,
        {},
        { withCredentials: true, timeout: 60000 },
      );
      toast.dismiss(toastId);
      setTitleSuggestion({ chapter_id: c.id, suggestions: r.data.suggestions || [] });
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err?.response?.data?.detail || 'Could not suggest title.');
    } finally {
      setSuggestingTitleId(null);
    }
  };
  const acceptTitleSuggestion = async (title) => {
    if (!titleSuggestion) return;
    const { chapter_id } = titleSuggestion;
    const c = chapters.find((x) => x.id === chapter_id);
    if (!c) { setTitleSuggestion(null); return; }
    setTitleSuggestion(null);
    setSavingId(chapter_id);
    try {
      const updated = await chaptersApi.update(chapter_id, { title });
      setChapters((prev) => prev.map((row) => row.id === chapter_id ? { ...row, ...updated } : row));
      toast.success('Chapter title updated.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save title.');
    } finally { setSavingId(null); }
  };

  // ── Edit Walker (admin-only) ──────────────────────────────────────────────
  const [walkerOpen, setWalkerOpen] = useState(false);
  const isAdmin = !!(user?.is_admin || user?.role === 'admin' || user?.role === 'super_admin');

  // ── Final chapter navigation reminder ────────────────────────────────────
  // Show a one-time informational toast per session when the writer
  // navigates to the very last chapter so they remember the wrap-up contract.
  const finalChapterToastShownRef = useRef(false);

  // ── Arc Walker ────────────────────────────────────────────────────────────
  const [arcWalkerOpen, setArcWalkerOpen] = useState(false);
  const arcWalkerResult = project?.arc_walker_result || null;
  const arcScore = arcWalkerResult?.publishable_score ?? null;
  // Quiet mode — stored in localStorage so it persists across panel remounts
  // but resets when the user navigates away (session-level).
  const [arcQuiet, setArcQuiet] = useState(
    () => window.localStorage.getItem('loom:arc_quiet') === '1'
  );
  const toggleArcQuiet = () => {
    const next = !arcQuiet;
    setArcQuiet(next);
    window.localStorage.setItem('loom:arc_quiet', next ? '1' : '0');
  };

  // ── Coach Actions dialog ──────────────────────────────────────────────────
  const [coachDialogChapter, setCoachDialogChapter] = useState(null); // chapter object
  const [coachDialogHint, setCoachDialogHint] = useState(null);       // {label, body}

  // ── Full Novel Review (Publisher+) ───────────────────────────────────────
  const { can } = useCapabilities();
  const [fullReviewOpen, setFullReviewOpen] = useState(false);

  // Background poller — checks every 60 s whether a running review has
  // finished so we can toast the user even if the drawer is closed.
  const lastReviewStatus = useRef(null);
  const [reviewBadge, setReviewBadge] = useState(false);

  useEffect(() => {
    if (!project?.id) return undefined;
    let cancelled = false;
    const check = async () => {
      try {
        const r = await import('@/lib/api').then((m) => m.userContinuityApi.get(project.id));
        if (cancelled) return;
        const status = r?.job?.status;
        const prev   = lastReviewStatus.current;
        if (
          (prev === 'running' || prev === 'queued') &&
          status === 'done'
        ) {
          const needs = r?.needs_rework_count || 0;
          toast.success(
            needs > 0
              ? `Full review done — ${needs} chapter${needs === 1 ? '' : 's'} flagged.`
              : 'Full review complete — all chapters look good!',
            { duration: 8000 }
          );
          setReviewBadge(true);
        }
        lastReviewStatus.current = status;
      } catch { /* silent — user may not have publisher tier yet */ }
    };
    check();
    const t = setInterval(check, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [project?.id]);

  const total = chapters.reduce((a, c) => a + (c.words || 0), 0);
  const targetWords = project?.target_words || project?.targetWords || (chapters.length * 2500);
  const chaptersCount = project?.chapters_count || chapters.length;

  // iter55z-e — Auto-draft progress banner. Polls the project every 8s
  // while `auto_draft_status === "in_progress"` so chapters appear in
  // real time as the background task finishes each one. Shows one
  // completion toast when the status flips to "done".
  const autoStatus = project?.auto_draft_status;
  const doneToastedRef = useRef(false);
  useEffect(() => {
    if (!project?.id) return undefined;
    if (autoStatus !== 'in_progress') return undefined;
    const t = setInterval(() => {
      loadProject(project.id, { silent: true });
    }, 8000);
    return () => clearInterval(t);
  }, [project?.id, autoStatus, loadProject]);
  useEffect(() => {
    if (autoStatus === 'done' && !doneToastedRef.current) {
      doneToastedRef.current = true;
      toast.success(`Novel draft complete — ${(project?.auto_draft_total_words || 0).toLocaleString()} words across ${chaptersCount} chapters.`);
    }
    if (autoStatus === 'error' && !doneToastedRef.current) {
      doneToastedRef.current = true;
      toast.error(`Auto-draft stopped: ${project?.auto_draft_error || 'unknown error'}`);
    }
    if (autoStatus === 'in_progress') doneToastedRef.current = false;
  }, [autoStatus, project?.auto_draft_total_words, project?.auto_draft_error, chaptersCount]);

  const draftedCount = chapters.filter(
    (c) => c.status === 'draft' || c.status === 'final' || (c.words || 0) > 0
  ).length;
  const draftPct = chaptersCount > 0
    ? Math.min(100, Math.round((draftedCount / chaptersCount) * 100))
    : 0;
  const nextIdx = Math.min(chaptersCount, draftedCount + 1);

  // iter55y — "This is my ending" toggle. Marks the chapter as the
  // manuscript's final chapter so the wordcount system can award the
  // multi-colored badge even under 80k words. Only one chapter can be
  // the ending at a time — flipping it on clears every sibling.
  const toggleEnding = async (chapter, e) => {
    e.stopPropagation();
    const nextValue = !chapter.is_ending;
    setSavingId(chapter.id);
    try {
      // Clear siblings first if setting a new ending.
      if (nextValue && setChapters) {
        const others = chapters.filter((c) => c.id !== chapter.id && c.is_ending);
        for (const o of others) {
          try { await chaptersApi.update(o.id, { is_ending: false }); } catch { /* keep going */ }
        }
      }
      const updated = await chaptersApi.update(chapter.id, { is_ending: nextValue });
      if (setChapters) {
        setChapters((prev) => prev.map((c) => {
          if (c.id === chapter.id) return { ...c, ...updated, is_ending: nextValue };
          if (nextValue && c.is_ending) return { ...c, is_ending: false };
          return c;
        }));
      }
      toast.success(nextValue ? 'Marked as “The End”.' : 'Ending mark removed.');
      // Refresh badge state (multi-colored leaf fires when is_ending flips on).
      try { window.dispatchEvent(new CustomEvent('pw:chapter-saved', { detail: { cid: chapter.id } })); } catch { /* ignore */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update ending.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <PanelFrame
      title="Chapters"
      icon={BookText}
      panelId="chapters"
      badge={`${chapters.length} / ${chaptersCount}`}
      onClose={onClose}
      actions={
        <>
          {/* Arc Score badge — always-visible pill so it's glanceable without opening the dropdown */}
          {arcScore !== null && (
            <span
              className={cn(
                'shrink-0 rounded-sm px-1.5 py-0.5 text-[10px] font-bold text-white',
                arcScore >= 85 ? 'bg-emerald-600' : arcScore >= 70 ? 'bg-amber-500' : 'bg-rose-500',
              )}
              data-testid="chapters-arc-score-badge"
              title={`Arc score: ${arcScore} — click Arc Walker in the menu for details`}
            >
              Arc {arcScore}
            </span>
          )}
          {/* ── Single unified dropdown — keeps the header clear at any width ── */}
          <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="relative h-6 gap-1 px-1.5 text-[12px] text-muted-foreground hover:text-foreground"
              data-testid="chapters-tools-dropdown-btn"
              title="Chapter options — View, New Chapter, Tools"
            >
              {arcScore !== null && (
                <span
                  className={cn(
                    'absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-0.5 text-[9px] font-semibold text-white',
                    arcScore >= 85 ? 'bg-emerald-600' : arcScore >= 70 ? 'bg-amber-500' : 'bg-rose-500',
                  )}
                  data-testid="tools-btn-arc-badge"
                >{arcScore}</span>
              )}
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">

            {/* ── View mode ── */}
            <DropdownMenuItem
              onSelect={() => { if (!titlesVisible) toggleTitlesVisible(); }}
              disabled={togglingTitles}
              data-testid="chapter-titles-toggle-titled"
              className="flex items-center gap-2 text-[13px]"
            >
              <Type className="h-3.5 w-3.5 text-primary" />
              Titled
              {titlesVisible && <span className="ml-auto text-primary">✓</span>}
            </DropdownMenuItem>

            <DropdownMenuItem
              onSelect={() => { if (titlesVisible) toggleTitlesVisible(); }}
              disabled={togglingTitles}
              data-testid="chapter-titles-toggle-numbered"
              className="flex items-center gap-2 text-[13px]"
            >
              <Hash className="h-3.5 w-3.5 text-primary" />
              Numbered
              {!titlesVisible && <span className="ml-auto text-primary">✓</span>}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* ── New Chapter ── */}
            <DropdownMenuItem
              onSelect={addChapter}
              disabled={addingChapter}
              data-testid="chapters-add-btn"
              className="flex items-center gap-2 text-[13px]"
            >
              {addingChapter
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Plus className="h-3.5 w-3.5 text-primary" />}
              New Chapter
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* ── Analysis & Tools ── */}
            <DropdownMenuItem
              onSelect={() => setAnalyzeOpen(true)}
              data-testid="chapters-analyze-btn"
              className="flex items-center gap-2 text-[13px]"
            >
              <TrendingUp className="h-3.5 w-3.5 text-primary" />
              Analyze
              {totalWeak > 0 && (
                <span className="ml-auto inline-flex min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[9.5px] font-semibold text-white">
                  {totalWeak}
                </span>
              )}
            </DropdownMenuItem>

            <DropdownMenuItem
              onSelect={() => setVoiceOpen(true)}
              data-testid="chapters-voice-coach-btn"
              className="flex items-center gap-2 text-[13px]"
            >
              <Mic className="h-3.5 w-3.5 text-primary" />
              Voice
            </DropdownMenuItem>

            <DropdownMenuItem
              onSelect={() => setArcWalkerOpen(true)}
              data-testid="chapters-arc-walker-btn"
              className="flex items-center gap-2 text-[13px]"
            >
              <GitBranch className={cn(
                'h-3.5 w-3.5',
                arcScore === null ? 'text-primary'
                  : arcScore >= 85 ? 'text-emerald-600'
                  : arcScore >= 70 ? 'text-amber-600'
                  : 'text-rose-600',
              )} />
              Arc Walker
              {arcScore !== null && (
                <span className={cn(
                  'ml-auto inline-flex min-w-[16px] items-center justify-center rounded-full px-1 text-[9.5px] font-semibold text-white',
                  arcScore >= 85 ? 'bg-emerald-600' : arcScore >= 70 ? 'bg-amber-500' : 'bg-rose-500',
                )}>
                  {arcScore}
                </span>
              )}
            </DropdownMenuItem>

            <DropdownMenuItem
              onSelect={toggleArcQuiet}
              data-testid="arc-quiet-toggle"
              className="flex items-center gap-2 text-[13px]"
            >
              <Eye className={cn('h-3.5 w-3.5', arcQuiet ? 'opacity-40' : 'text-muted-foreground')} />
              {arcQuiet ? 'Resume Arc auto-run' : 'Pause Arc auto-run'}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {isAdmin && (
              <DropdownMenuItem
                onSelect={() => setWalkerOpen(true)}
                data-testid="chapters-edit-walker-btn"
                className="flex items-center gap-2 text-[13px] text-amber-600"
              >
                <Zap className="h-3.5 w-3.5" />
                Edit Walker
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              onSelect={() => {
                if (!can('full_novel_review')) return;
                setFullReviewOpen(true);
                setReviewBadge(false);
              }}
              data-testid="chapters-full-review-btn"
              disabled={!can('full_novel_review')}
              className="flex items-center gap-2 text-[13px]"
            >
              {can('full_novel_review')
                ? <ShieldCheck className="h-3.5 w-3.5 text-indigo-500" />
                : <Lock className="h-3 w-3 text-muted-foreground/50" />}
              Review
              {reviewBadge && can('full_novel_review') && (
                <span className="ml-auto h-2 w-2 rounded-full bg-emerald-500" />
              )}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onSelect={runVoiceDriftBatchCheck}
              disabled={batchDriftBusy}
              data-testid="chapters-drift-batch-btn"
              className="flex items-center gap-2 text-[13px]"
            >
              {batchDriftBusy
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Mic className="h-3 w-3 text-muted-foreground/70" />}
              {batchDriftBusy ? 'Checking…' : 'Check All (Voice)'}
            </DropdownMenuItem>

            {project?.id && (
              <DropdownMenuItem
                onSelect={() => {}}
                className="p-0"
                data-testid="chapters-plot-all-item"
              >
                <div className="flex w-full items-center gap-2 px-2 py-1.5 text-[13px]" onClick={(e) => e.stopPropagation()}>
                  <PlotReadinessPill projectId={project.id} stale={!!project?.plot_readiness_stale} inDropdown />
                </div>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        </>
      }
    >
      <div className="flex h-full flex-col">
        {/* ── Global manuscript rename ─────────────────────────────── */}
        <div className="border-b border-panel-border bg-panel-header/80 px-3 py-1.5">
          {globalRenaming ? (
            <div className="flex items-center gap-1">
              <Input
                ref={globalRenameRef}
                value={globalRenameDraft}
                onChange={(e) => setGlobalRenameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitGlobalRename(); }
                  if (e.key === 'Escape') { e.preventDefault(); cancelGlobalRename(); }
                }}
                onBlur={commitGlobalRename}
                maxLength={200}
                placeholder={t('chapters.manuscriptTitlePlaceholder')}
                className="h-6 flex-1 border-primary/50 px-1.5 py-0 text-[13.5px] font-medium"
                data-testid="global-rename-input"
              />
              <button
                type="button"
                onClick={commitGlobalRename}
                disabled={globalRenameSaving}
                className="rounded p-0.5 text-emerald-700 hover:bg-emerald-50 transition"
                title={t('chapters.saveTitle')}
                data-testid="global-rename-confirm-btn"
              >
                {globalRenameSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                onClick={cancelGlobalRename}
                className="rounded p-0.5 text-muted-foreground hover:bg-secondary transition"
                title={t('common.cancel')}
                data-testid="global-rename-cancel-btn"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div className="group flex items-center gap-1.5">
              <span
                className="flex-1 truncate text-[13.5px] font-medium text-foreground/80 cursor-text"
                data-testid="manuscript-title-display"
                title={project?.title || project?.name || 'Untitled manuscript'}
                onDoubleClick={startGlobalRename}
              >
                {project?.title || project?.name || 'Untitled manuscript'}
              </span>
              <button
                type="button"
                onClick={startGlobalRename}
                className="opacity-60 hover:opacity-100 transition rounded p-0.5 text-muted-foreground hover:text-primary"
                title={t('chapters.renameMSTooltip')}
                data-testid="global-rename-btn"
              >
                <Edit3 className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
        {/* Plot Completeness Banner — shows when narrative elements are missing/weak */}
        {project?.plot_readiness && (
          <PlotCompletenessBanner
            projectId={project.id}
            plotReadiness={project.plot_readiness}
            stale={!!project?.plot_readiness_stale}
          />
        )}
        {/* Title suggestion inline banner — 3 alternatives */}
        {titleSuggestion && (
          <div
            className="border-b border-primary/30 bg-primary/5 px-3 py-2"
            data-testid="title-suggestion-banner"
          >
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <Wand2 className="h-3.5 w-3.5 text-primary" />
                <span className="text-[13px] font-semibold text-primary">
                  3 title suggestions for Ch {chapters.find(x => x.id === titleSuggestion.chapter_id)?.number ?? '?'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setTitleSuggestion(null)}
                className="rounded p-0.5 text-muted-foreground hover:text-foreground transition"
                data-testid="title-suggestion-dismiss-btn"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <div className="flex flex-col gap-1">
              {(titleSuggestion.suggestions || []).map((s, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 rounded-sm border border-primary/20 bg-background/70 px-2 py-1.5 hover:border-primary/50 hover:bg-primary/5 transition group cursor-pointer"
                  onClick={() => acceptTitleSuggestion(s.title)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') acceptTitleSuggestion(s.title); }}
                  data-testid={`title-suggestion-option-${i}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] font-semibold text-foreground truncate group-hover:text-primary transition">
                      &ldquo;{s.title}&rdquo;
                    </p>
                    {s.reasoning && (
                      <p className="mt-0.5 text-[13px] text-muted-foreground leading-snug">{s.reasoning}</p>
                    )}
                  </div>
                  <span className="shrink-0 rounded px-1.5 py-0.5 text-[9.5px] font-medium bg-primary/10 text-primary opacity-0 group-hover:opacity-100 transition">
                    Apply
                  </span>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const c = chapters.find((x) => x.id === titleSuggestion.chapter_id);
                  if (c) startRename(c, null);
                  setTitleSuggestion(null);
                }}
                className="mt-0.5 text-left text-[13px] text-muted-foreground hover:text-primary transition"
                data-testid="title-suggestion-edit-btn"
              >
                None of these? Type your own →
              </button>
            </div>
          </div>
        )}
        <div className="border-b border-panel-border bg-panel-header/60 px-3 py-2">
          <div className="text-[13px] uppercase tracking-wider text-muted-foreground">Target {targetWords.toLocaleString()} words</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="font-mono-stat text-[15px] font-semibold text-foreground">{total.toLocaleString()}</span>
            <span className="font-mono-stat text-[13px] text-muted-foreground">/ {targetWords.toLocaleString()}</span>
          </div>
          <div className="mt-1.5 h-1 overflow-hidden rounded-sm bg-secondary">
            <div className="h-full bg-gradient-to-r from-primary to-primary/70" style={{ width: `${Math.min(100, (total / targetWords) * 100)}%` }} />
          </div>
        </div>
        {/* 2026-02 — Series Continuation Settings. When enabled, the final-chapter
            wrap-up prompt plants a hook into the next book rather than hard-closing. */}
        <SeriesSettingsRow project={project} updateProject={updateProject} />
        {/* 2026-02 — Voice Sample paste box. Writer pastes their own prose to
            establish stylistic voice for every chapter draft in this project. */}
        <VoiceSampleRow project={project} updateProject={updateProject} />
        {/* Streaming chapter indicator — shown when a single chapter is being AI-written
            (but the full auto-draft is not running, so the autodraft banner won't show). */}
        {streamingChapterId && autoStatus !== 'in_progress' && (
          <div
            className="flex items-center gap-2 border-b border-primary/25 bg-primary/8 px-3 py-1.5"
            data-testid="chapter-streaming-banner"
          >
            <Loader2 className="h-3 w-3 animate-spin text-primary shrink-0" />
            <span className="text-[12px] font-medium text-primary/90">
              AI is writing a chapter…
            </span>
          </div>
        )}
        {/* Global build banner — shown when auto-draft is not running but building=true */}
        {building && !streamingChapterId && autoStatus !== 'in_progress' && (
          <div
            className="flex items-center gap-2 border-b border-primary/25 bg-primary/8 px-3 py-1.5"
            data-testid="project-building-banner"
          >
            <Loader2 className="h-3 w-3 animate-spin text-primary shrink-0" />
            <span className="text-[12px] font-medium text-primary/90">
              Building your project…
            </span>
          </div>
        )}
        {/* iter55z-e — Auto-draft progress banner. */}
        {autoStatus === 'in_progress' && (
          <div
            className="border-b border-primary/30 bg-primary/5 px-3 py-2"
            data-testid="autodraft-progress-banner"
          >
            <div className="flex items-center gap-1.5 text-[13px] font-medium text-primary">
              <Loader2 className="h-3 w-3 animate-spin" />
              Drafting chapter {nextIdx} of {chaptersCount}
              <span className="ml-auto font-mono-stat text-[13px]">{draftPct}%</span>
            </div>
            <div className="mt-1 h-1 overflow-hidden rounded-sm bg-primary/15">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${draftPct}%` }}
                data-testid="autodraft-progress-bar"
              />
            </div>
            <div className="mt-0.5 text-[13px] text-muted-foreground">
              Each chapter takes ~15-30s. You can keep working while it runs.
            </div>
          </div>
        )}
        {autoStatus === 'done' && draftedCount === chaptersCount && (
          <div
            className="flex items-center gap-1.5 border-b border-emerald-300/60 bg-emerald-50 px-3 py-1.5 text-[13px] font-medium text-emerald-900"
            data-testid="autodraft-done-banner"
          >
            <PartyPopper className="h-3 w-3" />
            Novel draft complete · {(project?.auto_draft_total_words || total).toLocaleString()} words
          </div>
        )}
        <ul className="flex-1 overflow-auto scroll-thin py-1">
          {chapters.map((c) => {
            const s = STATUS[c.status];
            const active = c.id === activeChapterId;
            const Icon = s.icon;
            const isEnding = !!c.is_ending;
            const isFinalChapter = c.number === chaptersCount;
            const saving = savingId === c.id;
            const rowHint = chapterRowHint({
              number: c.number,
              wordCount: c.words || 0,
              status: c.status,
              isActive: active,
              totalChapters: chapters.length,
            });
            const weakCount = weakByChapter[c.id] || 0;
            return (
              <li key={c.id}>
                <CoachHint
                  {...rowHint}
                  side="right"
                  align="center"
                  testId={`coach-chapter-row-${c.id}`}
                  onActionsClick={() => {
                    setCoachDialogChapter(c);
                    setCoachDialogHint(rowHint);
                  }}
                >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setActiveChapterId(c.id);
                    if (isFinalChapter && !finalChapterToastShownRef.current) {
                      finalChapterToastShownRef.current = true;
                      toast.info(
                        `Final chapter — wrap up all open story arcs and bring every character journey to a close.`,
                        { duration: 7000, icon: <BookOpen className="h-4 w-4 text-rose-600" /> }
                      );
                    }
                  }}
                  onDoubleClick={(e) => {
                    if (titlesVisible && renamingId !== c.id) startRename(c, e);
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveChapterId(c.id); } }}
                  className={cn(
                    'group flex w-full cursor-pointer items-start gap-2 border-l-2 border-transparent px-3 py-1.5 text-left transition-colors hover:bg-secondary/60 focus:outline-none focus:ring-1 focus:ring-primary/40',
                    active && 'border-primary bg-secondary/80',
                    streamingChapterId === c.id && 'border-primary/60 bg-primary/5 animate-pulse-subtle',
                    isEnding && 'bg-amber-50/40',
                    isFinalChapter && !isEnding && 'bg-rose-50/20',
                  )}
                >
                  <span className="mt-0.5 font-mono-stat text-[13px] text-muted-foreground w-5">{String(c.number).padStart(2, '0')}</span>
                  {/* Word-count ring — replaced by orbit spinner while this chapter is being AI-written */}
                  {streamingChapterId === c.id ? (
                    <span
                      className="shrink-0 inline-flex items-center justify-center w-[18px] h-[18px]"
                      data-testid={`chapter-streaming-spinner-${c.id}`}
                      aria-label="AI is writing this chapter"
                    >
                      <Loader2 className="h-[14px] w-[14px] animate-spin text-primary" />
                    </span>
                  ) : (
                    <WordRing
                      words={c.words || 0}
                      target={project?.words_per_chapter || Math.round(targetWords / Math.max(1, chaptersCount))}
                      data-testid={`chapter-word-ring-${c.id}`}
                    />
                  )}
                  <span className="flex-1 min-w-0">
                    <span className={cn('flex items-center gap-1.5 truncate text-[14px] font-medium', active ? 'text-foreground' : 'text-foreground/85')}>
                      {renamingId === c.id ? (
                        <Input
                          ref={renameInputRef}
                          value={renameDraft}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') { e.preventDefault(); commitRename(c); }
                            if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                          }}
                          onBlur={() => commitRename(c)}
                          maxLength={120}
                          className="h-6 flex-1 border-primary/40 px-1.5 py-0 text-[14px]"
                          data-testid={`chapter-rename-input-${c.id}`}
                        />
                      ) : (
                        <>
                          <span
                            className="truncate"
                            title={titlesVisible ? t('chapters.doubleClickRename') : t('chapters.numberedModeTitle')}
                            data-testid={`chapter-title-${c.id}`}
                          >
                            {chapterLabel(c, project)}
                          </span>
                          {/* iter55f86 — Hover-only rename affordance. Hidden in Numbered mode. */}
                          {titlesVisible && (
                            <button
                              type="button"
                              onClick={(e) => startRename(c, e)}
                              className="opacity-0 transition group-hover:opacity-100 text-muted-foreground hover:text-primary"
                              title={t('chapters.renameChapter')}
                              data-testid={`chapter-rename-btn-${c.id}`}
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                          )}
                          {/* AI chapter title wand — suggests a title from chapter content */}
                          {titlesVisible && (c.words || 0) > 0 && (
                            <button
                              type="button"
                              onClick={(e) => suggestTitle(c, e)}
                              disabled={!!suggestingTitleId}
                              className={cn(
                                'transition rounded text-muted-foreground hover:text-primary',
                                suggestingTitleId === c.id
                                  ? 'opacity-100'
                                  : 'opacity-0 group-hover:opacity-100',
                              )}
                              title={t('chapters.suggestTitleTooltip')}
                              data-testid={`chapter-suggest-title-btn-${c.id}`}
                            >
                              {suggestingTitleId === c.id
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : <Wand2 className="h-3 w-3" />}
                            </button>
                          )}
                        </>
                      )}
                      {isEnding && (
                        <span className="inline-flex items-center gap-0.5 rounded-sm bg-amber-100 px-1 text-[9.5px] font-semibold uppercase tracking-wide text-amber-900" data-testid={`chapter-ending-badge-${c.id}`}>
                          <Flag className="h-2.5 w-2.5" /> The End
                        </span>
                      )}
                      {/* Final-chapter indicator — reminds writer this is the story's conclusion */}
                      {isFinalChapter && !isEnding && (
                        <span
                          className="inline-flex items-center gap-0.5 rounded-sm bg-rose-100 px-1 text-[9.5px] font-semibold uppercase tracking-wide text-rose-800"
                          data-testid={`chapter-final-badge-${c.id}`}
                          title="This is the final chapter — wrap up all open narrative arcs"
                        >
                          <BookOpen className="h-2.5 w-2.5" /> Final
                        </span>
                      )}
                      {/* iter-p2-p — reader-journey drop-off heatmap */}
                      {heatByChapter[c.number] && (
                        <span
                          className={cn(
                            'ml-auto inline-flex items-center rounded-sm px-1 text-[9.5px] font-semibold tracking-wide',
                            heatByChapter[c.number].drop_pct >= 40
                              ? 'bg-rose-100 text-rose-900'
                              : heatByChapter[c.number].drop_pct >= 20
                                ? 'bg-amber-100 text-amber-900'
                                : 'bg-emerald-100 text-emerald-900',
                          )}
                          title={`${heatByChapter[c.number].drop_to_next || 0} readers stopped after this chapter (${heatByChapter[c.number].drop_pct.toFixed(1)}% drop-off).`}
                          data-testid={`chapter-heat-${c.id}`}
                        >
                          <TrendingUp className="mr-0.5 h-2.5 w-2.5" />
                          {heatByChapter[c.number].drop_pct.toFixed(0)}%
                        </span>
                      )}
                      {/* iter-p3-u — AI-vs-human provenance badge */}
                      {aiByChapter[c.id] && aiByChapter[c.id].actions_total > 0 && (
                        <span
                          className={cn(
                            'inline-flex items-center gap-0.5 rounded-sm px-1 text-[9.5px] font-semibold tracking-wide',
                            !heatByChapter[c.number] && 'ml-auto',
                            aiByChapter[c.id].ai_share_pct >= 66
                              ? 'bg-amber-100 text-amber-900'
                              : aiByChapter[c.id].ai_share_pct >= 34
                                ? 'bg-emerald-100 text-emerald-900'
                                : 'bg-sky-100 text-sky-900',
                          )}
                          title={`${aiByChapter[c.id].ai_actions} AI · ${aiByChapter[c.id].human_actions} human — ${aiByChapter[c.id].ai_share_pct}% AI share (from Editing Record)`}
                          data-testid={`chapter-ai-share-${c.id}`}
                        >
                          <Wand2 className="mr-0.5 h-2.5 w-2.5" />
                          {aiByChapter[c.id].ai_share_pct}% AI
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 flex items-center gap-1.5 text-[13px] text-muted-foreground">
                      <Icon className={cn('h-3 w-3', `text-${s.color}`)} />
                      <span>{t(s.tKey)}</span>
                      <span>·</span>
                      <span className="font-mono-stat">{c.words.toLocaleString()}w</span>
                      {weakCount > 0 && (
                        <span
                          className="inline-flex items-center gap-0.5 rounded-sm bg-amber-100 px-1 py-0.5 text-[9.5px] font-semibold text-amber-900"
                          data-testid={`chapter-weak-badge-${c.id}`}
                          title={`${weakCount} pending transition fix${weakCount === 1 ? '' : 'es'} on this chapter`}
                        >
                          <Wand2 className="h-2.5 w-2.5" />
                          {weakCount} weak
                        </span>
                      )}
                      {/* Revision Mode accepted-note count badge */}
                      {(revisionCounts[c.id] || 0) > 0 && (
                        <span
                          className="inline-flex items-center gap-0.5 rounded-sm bg-emerald-100 px-1 py-0.5 text-[9.5px] font-semibold text-emerald-800"
                          data-testid={`chapter-revision-badge-${c.id}`}
                          title={`${revisionCounts[c.id]} revision suggestion${revisionCounts[c.id] === 1 ? '' : 's'} accepted in Revision Mode`}
                        >
                          <Check className="h-2.5 w-2.5" />
                          {revisionCounts[c.id]}
                        </span>
                      )}
                      {/* iter55y — Toggle "This is my ending" */}
                      <button
                        type="button"
                        onClick={(e) => toggleEnding(c, e)}
                        disabled={saving}
                        data-testid={`chapter-ending-toggle-${c.id}`}
                        className={cn(
                          'ml-auto inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[13px] transition',
                          isEnding
                            ? 'bg-amber-200 text-amber-900 hover:bg-amber-300'
                            : 'border border-dashed border-border text-muted-foreground opacity-0 group-hover:opacity-100 hover:border-primary hover:text-primary',
                          saving && 'opacity-50',
                        )}
                        title={isEnding ? t('chapters.unmarkEnding') : t('chapters.markEnding')}
                      >
                        <Flag className="h-2.5 w-2.5" />
                        {isEnding ? t('chapters.endingLabel') : t('chapters.markEndLabel')}
                      </button>
                      {/* Voice drift persistent badge — shown after a drift check runs */}
                      {driftResults[c.id] && (
                        <span
                          data-testid={`chapter-drift-badge-${c.id}`}
                          title={driftResults[c.id].drifted
                            ? `Voice drift detected (${driftResults[c.id].severity}) — click the Voice button to re-check`
                            : t('chapters.voiceCheckPassed')}
                          className={cn(
                            'inline-flex items-center gap-0.5 rounded-sm px-1.5 py-0.5 text-[9.5px] font-semibold',
                            driftResults[c.id].drifted
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-emerald-100 text-emerald-800',
                          )}
                        >
                          <Mic className="h-2.5 w-2.5" />
                          {driftResults[c.id].drifted ? `${driftResults[c.id].severity || 'drift'}` : 'ok'}
                        </span>
                      )}

                      {/* iter-p3-u — Regional-voice drift check */}
                      <button
                        type="button"
                        onClick={(e) => runVoiceDriftCheck(c, e)}
                        disabled={voiceDriftBusy === c.id}
                        data-testid={`chapter-voice-drift-${c.id}`}
                        className={cn(
                          'inline-flex items-center gap-1 rounded-sm border border-dashed border-border px-1.5 py-0.5 text-[13px] text-muted-foreground opacity-0 group-hover:opacity-100 hover:border-primary hover:text-primary transition',
                          voiceDriftBusy === c.id && 'opacity-100',
                        )}
                        title="Check that this chapter's dialect + register match the character's Regional Voice preset."
                      >
                        {voiceDriftBusy === c.id
                          ? <Loader2 className="h-2.5 w-2.5 animate-spin" />
                          : <Mic className="h-2.5 w-2.5" />}
                        Voice
                      </button>
                      {/* Delete chapter — single-click to confirm, second click to execute */}
                      {chapters.length > 1 && (
                        confirmDeleteId === c.id ? (
                          <span className="inline-flex items-center gap-0.5">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); deleteChapter(c.id); }}
                              disabled={deletingId === c.id}
                              data-testid={`chapter-delete-confirm-${c.id}`}
                              className="inline-flex items-center gap-1 rounded-sm bg-destructive/90 px-1.5 py-0.5 text-[13px] font-semibold text-white hover:bg-destructive transition"
                            >
                              {deletingId === c.id ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Trash2 className="h-2.5 w-2.5" />}
                              Delete?
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                              className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
                              data-testid={`chapter-delete-cancel-${c.id}`}
                              title="Cancel"
                            >
                              <X className="h-2.5 w-2.5" />
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(c.id); }}
                            data-testid={`chapter-delete-btn-${c.id}`}
                            className="inline-flex items-center gap-1 rounded-sm border border-dashed border-border px-1.5 py-0.5 text-[13px] text-muted-foreground opacity-0 group-hover:opacity-100 hover:border-destructive hover:text-destructive transition"
                            title="Delete chapter"
                          >
                            <Trash2 className="h-2.5 w-2.5" />
                          </button>
                        )
                      )}
                    </span>
                  </span>
                </div>
                </CoachHint>
              </li>
            );
          })}
        </ul>
      </div>
      <ArcWalkerDrawer
        open={arcWalkerOpen}
        onClose={() => setArcWalkerOpen(false)}
        projectId={project?.id}
        cachedResult={arcWalkerResult}
      />
      <WalkerDrawer
        open={walkerOpen}
        onClose={() => setWalkerOpen(false)}
        projectId={project?.id}
        isAdmin={isAdmin}
      />
      {/* Coach Actions dialog */}
      {coachDialogChapter && (
        <CoachHintDialog
          open={Boolean(coachDialogChapter)}
          onClose={() => { setCoachDialogChapter(null); setCoachDialogHint(null); }}
          chapterId={coachDialogChapter.id}
          chapterNumber={coachDialogChapter.number}
          chapterTitle={coachDialogChapter.title}
          hintLabel={coachDialogHint?.label}
          hintBody={coachDialogHint?.body}
          setChapters={setChapters}
        />
      )}
      <AnalyzeTransitionsDrawer
        open={analyzeOpen}
        onClose={() => setAnalyzeOpen(false)}
        projectId={project?.id}
      />
      <VoiceCoachDrawer
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        projectId={project?.id}
        deepLink={voiceDeepLink}
      />
      <VoiceRewritePreviewDialog
        open={!!voiceRewrite}
        onOpenChange={(v) => (!v ? setVoiceRewrite(null) : null)}
        project={project}
        chapter={voiceRewrite?.chapter}
        rewrite={voiceRewrite?.rewrite}
        onApplied={() => loadProject?.(project?.id, { silent: true })}
      />
      {/* Full Novel Review drawer — Publisher+ tier */}
      <ContinuityReviewDrawer
        open={fullReviewOpen}
        onClose={() => setFullReviewOpen(false)}
        project={project}
      />
    </PanelFrame>
  );
}
